/**
 * Production Order Detail Page
 * Manufacturing Module — order overview, phase stepper, lot/batch management
 *
 * Features:
 *   - Header: order number, status, BOM info, product info
 *   - Progress stepper: PREPARATION -> MANUFACTURING -> QC -> RECEIPT
 *   - Phase details: status, duration, assignee, scrap
 *   - Action buttons per phase: Start, Complete, Record Scrap
 *   - Lot/Batch section: produced batches with quality status
 *   - Status transitions: Plan, Start, Complete, Cancel
 */

import type {
  LotBatch,
  ProductionOrderStatus,
  ProductionPhase,
  ProductionPhaseStatus,
  ProductionPhaseType,
  QualityStatus,
} from '@/api/manufacturing';
import { completePhase, recordScrap, startPhase } from '@/api/manufacturing';
import { Button, Card, CardContent, Input } from '@/components/ui';
import { useProductionOrder, useUpdateProductionOrderStatus } from '@/hooks/use-manufacturing';
import { useTranslation } from '@kgc/i18n';
import { Badge } from '@kgc/ui';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// ─── Constants ──────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<ProductionOrderStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  PLANNED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  IN_PROGRESS: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  COMPLETED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  CANCELLED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const PHASE_STATUS_COLORS: Record<ProductionPhaseStatus, string> = {
  PENDING: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  IN_PROGRESS: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  COMPLETED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  SKIPPED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
};

const QUALITY_STATUS_COLORS: Record<QualityStatus, string> = {
  PENDING: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  PASSED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  FAILED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  PARTIAL: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
};

const PHASE_ORDER: ProductionPhaseType[] = ['PREPARATION', 'MANUFACTURING', 'QC', 'RECEIPT'];

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDateTime(dateStr: string | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('hu-HU');
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('hu-HU');
}

// ─── Component ──────────────────────────────────────────────────────────────

export function ProductionOrderDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { order, isLoading, error, refetch } = useProductionOrder(id);
  const { mutate: doUpdateStatus, isLoading: statusUpdating } = useUpdateProductionOrderStatus();

  // Scrap form state
  const [scrapPhaseId, setScrapPhaseId] = useState<string | null>(null);
  const [scrapQty, setScrapQty] = useState('');
  const [scrapReason, setScrapReason] = useState('');

  const handleStatusTransition = async (newStatus: ProductionOrderStatus) => {
    if (!id) return;
    if (newStatus === 'CANCELLED') {
      if (
        !window.confirm(t('common.confirmDelete') ?? 'Are you sure you want to cancel this order?')
      )
        return;
    }
    try {
      await doUpdateStatus(id, newStatus);
      await refetch();
    } catch {
      // Error handled in hook
    }
  };

  const handleStartPhase = async (phaseId: string) => {
    if (!id) return;
    try {
      await startPhase(id, phaseId);
      await refetch();
    } catch {
      console.error('Failed to start phase');
    }
  };

  const handleCompletePhase = async (phaseId: string) => {
    if (!id) return;
    try {
      await completePhase(id, phaseId);
      await refetch();
    } catch {
      console.error('Failed to complete phase');
    }
  };

  const handleRecordScrap = async () => {
    if (!id || !scrapPhaseId || !scrapQty) return;
    try {
      await recordScrap(id, {
        phaseId: scrapPhaseId,
        quantity: parseFloat(scrapQty),
        reason: scrapReason || undefined,
      });
      setScrapPhaseId(null);
      setScrapQty('');
      setScrapReason('');
      await refetch();
    } catch {
      console.error('Failed to record scrap');
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">
            {error ?? t('manufacturing.productionOrder.notFound')}
          </p>
          <Button
            variant="ghost"
            onClick={() => navigate('/manufacturing/orders')}
            className="mt-2"
          >
            {t('common.back')}
          </Button>
        </div>
      </div>
    );
  }

  // Sort phases by phase order
  const sortedPhases = [...order.phases].sort((a, b) => {
    const aIdx = PHASE_ORDER.indexOf(a.phaseType);
    const bIdx = PHASE_ORDER.indexOf(b.phaseType);
    return aIdx - bIdx;
  });

  const progressPercent =
    order.totalPhases > 0 ? Math.round((order.completedPhases / order.totalPhases) * 100) : 0;

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="bg-white shadow-sm dark:bg-[#1a4f5c]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/manufacturing/orders')}>
              {t('common.back')}
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-foreground">{order.orderNumber}</h1>
                <Badge className={STATUS_COLORS[order.status]}>
                  {t(`manufacturing.status.${order.status.toLowerCase()}`)}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {order.outputProductName} — BOM: {order.bomCode}
              </p>
            </div>
          </div>

          {/* Status transition buttons */}
          <div className="flex gap-2">
            {order.status === 'DRAFT' && (
              <Button
                onClick={() => void handleStatusTransition('PLANNED')}
                disabled={statusUpdating}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {t('manufacturing.productionOrder.plan')}
              </Button>
            )}
            {order.status === 'PLANNED' && (
              <Button
                onClick={() => void handleStatusTransition('IN_PROGRESS')}
                disabled={statusUpdating}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {t('manufacturing.productionOrder.startProduction')}
              </Button>
            )}
            {order.status === 'IN_PROGRESS' && (
              <Button
                onClick={() => void handleStatusTransition('COMPLETED')}
                disabled={statusUpdating}
                className="bg-green-600 hover:bg-green-700"
              >
                {t('manufacturing.productionOrder.complete')}
              </Button>
            )}
            {(order.status === 'DRAFT' || order.status === 'PLANNED') && (
              <Button
                variant="ghost"
                onClick={() => void handleStatusTransition('CANCELLED')}
                disabled={statusUpdating}
                className="text-red-600 hover:text-red-700 dark:text-red-400"
              >
                {t('common.cancel')}
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Overview Cards */}
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t('manufacturing.productionOrder.plannedQty')}
              </p>
              <p className="text-2xl font-bold text-foreground">{order.plannedQuantity}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t('manufacturing.productionOrder.actualQty')}
              </p>
              <p className="text-2xl font-bold text-primary">{order.actualQuantity}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t('manufacturing.productionOrder.plannedStart')}
              </p>
              <p className="text-lg font-semibold text-foreground">
                {formatDate(order.plannedStart)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t('manufacturing.productionOrder.plannedEnd')}
              </p>
              <p className="text-lg font-semibold text-foreground">
                {formatDate(order.plannedEnd)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Progress Bar */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="font-medium text-foreground">
                {t('manufacturing.productionOrder.progress')}
              </span>
              <span className="text-muted-foreground">
                {order.completedPhases}/{order.totalPhases}{' '}
                {t('manufacturing.productionOrder.phasesCompleted')} ({progressPercent}%)
              </span>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className={`h-full rounded-full transition-all ${
                  progressPercent === 100 ? 'bg-green-500 dark:bg-green-400' : 'bg-primary'
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Phase Stepper */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <h2 className="mb-6 text-lg font-semibold text-foreground">
              {t('manufacturing.productionOrder.phases')}
            </h2>

            {/* Stepper row */}
            <div className="mb-6 flex items-center justify-between">
              {PHASE_ORDER.map((phaseType, idx) => {
                const phase = sortedPhases.find(p => p.phaseType === phaseType);
                const isCompleted = phase?.status === 'COMPLETED';
                const isActive = phase?.status === 'IN_PROGRESS';
                const isSkipped = phase?.status === 'SKIPPED';

                return (
                  <div key={phaseType} className="flex flex-1 items-center">
                    {/* Step circle */}
                    <div className="flex flex-col items-center">
                      <div
                        className={`flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-bold ${
                          isCompleted
                            ? 'border-green-500 bg-green-500 text-white dark:border-green-400 dark:bg-green-400'
                            : isActive
                              ? 'border-amber-500 bg-amber-500 text-white dark:border-amber-400 dark:bg-amber-400'
                              : isSkipped
                                ? 'border-blue-400 bg-blue-100 text-blue-700 dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-300'
                                : 'border-gray-300 bg-white text-muted-foreground dark:border-gray-600 dark:bg-slate-800'
                        }`}
                      >
                        {isCompleted ? (
                          <svg
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        ) : (
                          idx + 1
                        )}
                      </div>
                      <span className="mt-2 text-xs font-medium text-foreground">
                        {t(`manufacturing.phase.${phaseType.toLowerCase()}`)}
                      </span>
                    </div>

                    {/* Connector line */}
                    {idx < PHASE_ORDER.length - 1 && (
                      <div
                        className={`mx-2 h-0.5 flex-1 ${
                          isCompleted
                            ? 'bg-green-500 dark:bg-green-400'
                            : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Phase Details */}
            <div className="space-y-4">
              {sortedPhases.map(phase => (
                <PhaseCard
                  key={phase.id}
                  phase={phase}
                  orderId={order.id}
                  orderStatus={order.status}
                  onStart={() => void handleStartPhase(phase.id)}
                  onComplete={() => void handleCompletePhase(phase.id)}
                  onRecordScrap={() => setScrapPhaseId(phase.id)}
                  t={t}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Scrap Recording Modal (inline) */}
        {scrapPhaseId && (
          <Card className="mb-6 border-2 border-red-200 dark:border-red-800">
            <CardContent className="pt-6">
              <h2 className="mb-4 text-lg font-semibold text-foreground">
                {t('manufacturing.productionOrder.recordScrap')}
              </h2>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    {t('common.quantity')} *
                  </label>
                  <Input
                    type="number"
                    min={1}
                    value={scrapQty}
                    onChange={e => setScrapQty(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    {t('manufacturing.productionOrder.scrapReason')}
                  </label>
                  <Input
                    value={scrapReason}
                    onChange={e => setScrapReason(e.target.value)}
                    placeholder={t('manufacturing.productionOrder.scrapReasonPlaceholder')}
                  />
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button
                  onClick={() => void handleRecordScrap()}
                  disabled={!scrapQty}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {t('manufacturing.productionOrder.confirmScrap')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setScrapPhaseId(null);
                    setScrapQty('');
                    setScrapReason('');
                  }}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lot/Batch Section */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <h2 className="mb-4 text-lg font-semibold text-foreground">
              {t('manufacturing.lotBatch.title')}
            </h2>

            {order.lots.length === 0 ? (
              <p className="py-4 text-center text-muted-foreground">
                {t('manufacturing.lotBatch.noLots')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b dark:border-slate-700">
                      <th className="px-3 py-2 font-medium text-muted-foreground">
                        {t('manufacturing.lotBatch.lotNumber')}
                      </th>
                      <th className="px-3 py-2 font-medium text-muted-foreground">
                        {t('common.quantity')}
                      </th>
                      <th className="px-3 py-2 font-medium text-muted-foreground">
                        {t('manufacturing.lotBatch.qualityStatus')}
                      </th>
                      <th className="px-3 py-2 font-medium text-muted-foreground">
                        {t('manufacturing.lotBatch.producedAt')}
                      </th>
                      <th className="px-3 py-2 font-medium text-muted-foreground">
                        {t('common.notes')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.lots.map(lot => (
                      <LotRow key={lot.id} lot={lot} t={t} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

// ─── Phase Card ─────────────────────────────────────────────────────────────

interface PhaseCardProps {
  phase: ProductionPhase;
  orderId: string;
  orderStatus: ProductionOrderStatus;
  onStart: () => void;
  onComplete: () => void;
  onRecordScrap: () => void;
  t: (key: string, params?: Record<string, string>) => string;
}

function PhaseCard({ phase, orderStatus, onStart, onComplete, onRecordScrap, t }: PhaseCardProps) {
  const canStartPhase = orderStatus === 'IN_PROGRESS' && phase.status === 'PENDING';
  const canCompletePhase = orderStatus === 'IN_PROGRESS' && phase.status === 'IN_PROGRESS';

  return (
    <div className="flex items-center justify-between rounded-lg border p-4 dark:border-slate-700">
      <div className="flex-1">
        <div className="flex items-center gap-3">
          <span className="font-medium text-foreground">
            {t(`manufacturing.phase.${phase.phaseType.toLowerCase()}`)}
          </span>
          <Badge className={PHASE_STATUS_COLORS[phase.status]}>
            {t(`manufacturing.phaseStatus.${phase.status.toLowerCase()}`)}
          </Badge>
        </div>
        <div className="mt-1 flex flex-wrap gap-4 text-xs text-muted-foreground">
          {phase.assigneeName && (
            <span>
              {t('common.responsible')}: {phase.assigneeName}
            </span>
          )}
          {phase.startedAt && (
            <span>
              {t('manufacturing.productionOrder.started')}: {formatDateTime(phase.startedAt)}
            </span>
          )}
          {phase.completedAt && (
            <span>
              {t('manufacturing.productionOrder.completed')}: {formatDateTime(phase.completedAt)}
            </span>
          )}
          {phase.durationMinutes != null && phase.durationMinutes > 0 && (
            <span>
              {t('manufacturing.productionOrder.duration')}: {phase.durationMinutes} min
            </span>
          )}
          {phase.scrapQuantity > 0 && (
            <span className="text-red-600 dark:text-red-400">
              {t('manufacturing.productionOrder.scrap')}: {phase.scrapQuantity}
              {phase.scrapReason ? ` (${phase.scrapReason})` : ''}
            </span>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        {canStartPhase && (
          <Button size="sm" onClick={onStart}>
            {t('manufacturing.productionOrder.startPhase')}
          </Button>
        )}
        {canCompletePhase && (
          <Button size="sm" onClick={onComplete} className="bg-green-600 hover:bg-green-700">
            {t('manufacturing.productionOrder.completePhase')}
          </Button>
        )}
        {canCompletePhase && (
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700 dark:text-red-400"
            onClick={onRecordScrap}
          >
            {t('manufacturing.productionOrder.recordScrap')}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Lot Row ────────────────────────────────────────────────────────────────

interface LotRowProps {
  lot: LotBatch;
  t: (key: string, params?: Record<string, string>) => string;
}

function LotRow({ lot, t }: LotRowProps) {
  return (
    <tr className="border-b dark:border-slate-700">
      <td className="px-3 py-2 font-medium text-foreground">{lot.lotNumber}</td>
      <td className="px-3 py-2 text-foreground">{lot.quantity}</td>
      <td className="px-3 py-2">
        <Badge className={QUALITY_STATUS_COLORS[lot.qualityStatus]}>
          {t(`manufacturing.qualityStatus.${lot.qualityStatus.toLowerCase()}`)}
        </Badge>
      </td>
      <td className="px-3 py-2 text-muted-foreground">{formatDateTime(lot.producedAt)}</td>
      <td className="px-3 py-2 text-muted-foreground">{lot.notes ?? '-'}</td>
    </tr>
  );
}
